import PropTypes from 'prop-types';
import React from 'react';
import { convertLegacyContent, cleanState } from '@ublend-npm/aula-editor';
import { isAulaEditorState } from '@ublend-npm/aula-editor-utils';
import { analyticsTrackEvent } from '../../../../../../../../core/utils/analytics';
import {
  MESSAGE_CODE_FORMAT_USED,
  MESSAGE_EDITED,
  MESSAGE_FILE_UPLOADED,
} from '../../../../../../../../core/constants/analytics';

import {
  MESSAGE_CONTENT_TYPE,
  DONE,
  INBOX_EDIT_MESSAGE_TITLE,
} from '../../../../../../../constants/texts';
import * as types from '../../../types';
import onlyWhenDefined from '../../onlyWhenDefined';
import { flow } from '../../../utils';
import Dialog, {
  DialogTitle,
  DialogContent,
  DialogActions,
} from '../../../../../common/Dialog';
import EditZone from './EditZone';
import {
  TitleContainer,
  CreateIcon,
  EditZoneContainer,
} from './EditMessageModal.styled';

class EditMessageModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editorState: cleanState,
      isSubmitting: false,
      isScanning: false,
    };

    this.onReturn = this.onReturn.bind(this);
    this.onClick = this.onClick.bind(this);
    this.setEditorState = this.setEditorState.bind(this);
    this.setSubmittingState = this.setSubmittingState.bind(this);
    this.trackMessageAnalytics = this.trackMessageAnalytics.bind(this);
    this.handleFilesStatusChange = this.handleFilesStatusChange.bind(this);
  }

  componentDidMount() {
    const { message } = this.props;
    const parsedContent = JSON.parse(message.content);
    const initialState = isAulaEditorState(parsedContent)
      ? parsedContent
      : convertLegacyContent(parsedContent);

    this.setState((prevState) => ({
      ...prevState,
      editorState: initialState,
    }));
  }

  onClick() {
    const { editorState } = this.state;

    if (!this.state.isSubmitting && !this.state.isScanning) {
      this.onReturn(editorState);
    }
  }

  async onReturn() {
    const { save, close } = this.props;
    const { editorState, isSubmitting, isScanning } = this.state;
    if (!isSubmitting && !isScanning) {
      await save(JSON.stringify(editorState));

      // track events for analytics
      this.trackMessageAnalytics(editorState);
      close();
    }
  }

  setSubmittingState(isSubmitting) {
    this.setState((prevState) => ({
      ...prevState,
      isSubmitting,
    }));
  }
  setEditorState(editorState) {
    this.setState((prevState) => ({
      ...prevState,
      editorState,
    }));
  }

  trackMessageAnalytics(content) {
    const hasFile = content?.value?.some(
      (content) => content?.type === MESSAGE_CONTENT_TYPE.FILE
    );
    const hasCode = content?.value?.some(
      (content) =>
        content?.type === MESSAGE_CONTENT_TYPE.CODE ||
        content?.type === MESSAGE_CONTENT_TYPE.LATEX
    );
    if (hasFile) {
      analyticsTrackEvent(MESSAGE_FILE_UPLOADED);
    }
    if (hasCode) {
      analyticsTrackEvent(MESSAGE_CODE_FORMAT_USED);
    }
    // Trigger an analytics event for message edit
    analyticsTrackEvent(MESSAGE_EDITED);
  }

  handleFilesStatusChange({ isUpdating, isPersistable, hasError }) {
    console.log('HAVE_NO_ERROR_STATE', hasError, isUpdating, isPersistable);
    const isLoading = isUpdating || hasError || !isPersistable;
    if (this.state.isScanning !== isLoading) {
      this.setState({ isScanning: isLoading });
    }
  }

  render() {
    const { close, saving, editorConfig } = this.props;
    const { editorState, isSubmitting, isScanning } = this.state;

    return (
      <Dialog
        id="edit-message-modal"
        open
        onClose={close}
        maxWidth="md"
        style={{ zIndex: 1400 }}
        disableEnforceFocus
      >
        <DialogTitle id="edit-message-modal-dialogtitle" onClose={close}>
          <TitleContainer>
            <CreateIcon />
            {INBOX_EDIT_MESSAGE_TITLE}
          </TitleContainer>
        </DialogTitle>
        <DialogContent>
          <EditZoneContainer>
            <EditZone
              editorConfig={editorConfig}
              editorState={editorState}
              onChange={this.setEditorState}
              onReturn={this.onReturn}
              isSubmitting={isSubmitting}
              onFilesStatusChange={this.handleFilesStatusChange}
              setSubmittingState={this.setSubmittingState}
            />
          </EditZoneContainer>
        </DialogContent>
        <DialogActions
          primary={{
            label: DONE,
            loading: saving,
            disabled: isSubmitting || isScanning,
            onClick: this.onClick,
          }}
          secondary={{
            label: 'Cancel',
            onClick: close,
          }}
        />
      </Dialog>
    );
  }
}

EditMessageModal.propTypes = {
  close: PropTypes.func.isRequired,
  message: types.Message,
  save: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  editorConfig: PropTypes.shape({
    filesUrl: PropTypes.string,
    apiUrl: PropTypes.string,
  }).isRequired,
};

EditMessageModal.defaultProps = {
  message: undefined,
  saving: false,
};

const wrap = flow(onlyWhenDefined(['message']));

export default wrap(EditMessageModal);
